/* eslint-disable newline-per-chained-call */
/* eslint-disable prettier/prettier */
import React, { BaseSyntheticEvent, useCallback, useEffect } from "react";
import * as d3 from "d3";
import { ScaleTime, ZoomTransform } from "d3";
import ButtonGroup from "@mui/material/ButtonGroup";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import { styled } from "@iventis/styles";
import { Button } from "@mui/material";
import { filter, first, pairwise } from "rxjs/operators";
import { combineLatest } from "rxjs";
import { Group } from "@iventis/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { borderRadius } from "@iventis/styles/src/atomic-rules";
import { SimpleTooltip } from "@iventis/components";
import { isValidDate, useObservableValue } from "@iventis/utilities";
import { MAX_ZOOM_IN, MAX_ZOOM_OUT } from "../utilities/constants";
import { useCalendarState, useCalendarTheme, useCursor, useGroupAxisHeight, useGroupScale, useTimeScale, useViewportStream, useZoomLevelStream } from "./hooks";
import { useD3 } from "./use-d3";
import { TIME_AXIS_WIDTH, ViewportSizeData } from "./calendar-context";
import { GroupScale } from "./group-axis";

interface ZoomContainerProps {
    onClick?: (group: Group, time: Date) => void;
    onContextClick?: (group: Group, time: Date, event: PointerEvent) => void;
    zoomBehavior: d3.ZoomBehavior<SVGGraphicsElement, unknown>;
    onKeyButtonClicked?: (event: BaseSyntheticEvent) => void;
    keyOpen?: boolean;
    children?: React.ReactNode;
    resetZoomText?: string;
    initialStart: Date;
    initialEnd: Date;
}

export const ZoomContainer: React.FC<ZoomContainerProps> = React.memo(
    ({ zoomBehavior, children, onClick, onContextClick, onKeyButtonClicked, keyOpen, resetZoomText, initialStart, initialEnd }) => {
        const theme = useCalendarTheme();

        const ref = useD3(
            (svg) => {
                svg.call(zoomBehavior).on("dblclick.zoom", null);
            },
            [zoomBehavior]
        );

        const viewport$ = useViewportStream();
        const groupAxisHeight$ = useGroupAxisHeight();
        const groupScale$ = useGroupScale();
        const timeScale$ = useTimeScale();
        const cursor$ = useCursor();
        const zoomLevel$ = useZoomLevelStream();
        const zoomLevel = useObservableValue(zoomLevel$);
        const state$ = useCalendarState();

        const initialZoom = useCallback(() => {
            const svg = d3.select(ref.current);
            const sub = combineLatest([timeScale$.pipe(filter<ScaleTime<number, number, never>>(Boolean))]).subscribe(([timeScale]) => {
                const fullRange = timeScale.range();
                const zoomedRange = [Math.floor(timeScale(initialStart)), Math.floor(timeScale(initialEnd))];
                // Scale factor = full range diff, diveded by zoomed range diff (initial start/end times from URL for example)
                const scaleFactor = (fullRange[1] - fullRange[0]) / (zoomedRange[1] - zoomedRange[0]);
                // Only transforming y because the time axis is all we support for zooming
                const transformY = scaleFactor * zoomedRange[0] - fullRange[0];
                zoomBehavior.transform(svg, new ZoomTransform(scaleFactor, 0, -transformY));
            });
            sub.unsubscribe();
        }, [zoomBehavior, timeScale$]);

        useEffect(() => {
            const sub = state$.pipe(pairwise()).subscribe(([prev, next]) => {
                // Only zoom in on idle if we are transitioning from formatting to idle and we have valid initial date range
                if (prev === "formatting-calendar" && next === "idle" && isValidDate(initialStart) && isValidDate(initialEnd)) {
                    initialZoom();
                }
            });
            return () => sub.unsubscribe();
        }, [state$]);

        // update zoomed clip area when viewport dimensions change or group axis height changes
        useEffect(() => {
            const sub = combineLatest([viewport$.pipe(filter<ViewportSizeData>(Boolean)), groupAxisHeight$]).subscribe(([{ height, width }, groupAxisHeight]) => {
                const svg = d3.select(ref.current);
                svg.select("#zoomRect").attr("x", TIME_AXIS_WIDTH).attr("y", groupAxisHeight).attr("height", height).attr("width", width);

                svg.select("#clipviewport > rect").attr("x", TIME_AXIS_WIDTH).attr("y", groupAxisHeight).attr("height", height).attr("width", width);

                svg.select("#zoom-buttons")
                    .attr("x", width - 75)
                    .attr("y", height - 240);
            });

            return () => sub.unsubscribe();
        }, [viewport$]);

        useEffect(() => {
            const sub = cursor$.subscribe((cursor) => {
                d3.select(ref.current).style("cursor", cursor);
            });
            return () => sub.unsubscribe();
        }, [cursor$]);

        const onZoomOut = useCallback(() => {
            const svg = d3.select(ref.current);
            viewport$.pipe(filter<ViewportSizeData>(Boolean), first()).subscribe(({ height, width }) => {
                zoomBehavior.scaleBy(svg.transition().duration(300), 0.5, [width / 2, height / 2]);
            });
        }, [zoomBehavior, viewport$]);

        const onZoomIn = useCallback(() => {
            const svg = d3.select(ref.current);
            viewport$.pipe(filter<ViewportSizeData>(Boolean), first()).subscribe(({ height, width }) => {
                zoomBehavior.scaleBy(svg.transition().duration(300), 2, [width / 2, height / 2]);
            });
        }, [zoomBehavior, viewport$]);

        const clickHandler = useCallback(
            (event) => {
                combineLatest([timeScale$.pipe(filter<ScaleTime<number, number, never>>(Boolean)), groupScale$.pipe(filter<GroupScale>(Boolean))])
                    .pipe(first())
                    .subscribe(([timeScale, groupScale]) => {
                        const [x, y] = d3.pointer(event, event.target.closest("svg"));
                        const time = timeScale.invert(y);
                        const group = groupScale.getGroupAtCoord(x);
                        onClick?.(group, time);
                    });
            },
            [onClick]
        );

        const contextClickHandler = useCallback(
            (event) => {
                combineLatest([timeScale$.pipe(filter<ScaleTime<number, number, never>>(Boolean)), groupScale$.pipe(filter<GroupScale>(Boolean))])
                    .pipe(first())
                    .subscribe(([timeScale, groupScale]) => {
                        const [x, y] = d3.pointer(event, event.nativeEvent.target.closest("svg"));
                        const time = timeScale.invert(y);
                        const group = groupScale.getGroupAtCoord(x);
                        onContextClick?.(group, time, event.nativeEvent);
                    });

                event.preventDefault();
                event.stopPropagation();
                return false;
            },
            [onContextClick]
        );

        const onResetClicked = useCallback(() => {
            zoomBehavior.scaleTo(d3.select(ref.current).transition().duration(300), 1);
        }, [zoomBehavior, ref]);

        return (
            <g data-cy="zoomContainer" ref={ref}>
                <defs>
                    <clipPath id="clipviewport">
                        <rect />
                    </clipPath>
                </defs>
                <rect x={0} y={0} id="zoomRect" fill="transparent" strokeWidth={0} onClick={clickHandler} onContextMenu={contextClickHandler} />
                <g clipPath="url(#clipviewport)">{children}</g>
                <foreignObject style={{ color: theme?.iconButtonColours?.idle }} x={0} y={0} height={240} width={70} id="zoom-buttons">
                    <CalendarButtonsContainer color="inherit" orientation="vertical" variant="text">
                        <SimpleTooltip text={resetZoomText} placement="left">
                            <CalendarButton onClick={onResetClicked}>
                                <FontAwesomeIcon size="lg" icon={["far", "expand"]} />
                            </CalendarButton>
                        </SimpleTooltip>
                        <CalendarButton disabled={zoomLevel >= MAX_ZOOM_IN} data-cy="zoom-in-button" onClick={onZoomIn}>
                            <ZoomInIcon />
                        </CalendarButton>
                        <CalendarButton disabled={zoomLevel <= MAX_ZOOM_OUT} data-cy="zoom-out-button" onClick={onZoomOut}>
                            <ZoomOutIcon />
                        </CalendarButton>
                        <CalendarButton
                            disabled={onKeyButtonClicked == null}
                            onClick={(e: BaseSyntheticEvent) => onKeyButtonClicked(e)}
                            style={{ color: keyOpen ? theme?.iconButtonColours?.selected : "inherit" }}
                        >
                            <FontAwesomeIcon style={{ pointerEvents: "none" }} icon={["fas", "key"]} />
                        </CalendarButton>
                    </CalendarButtonsContainer>
                </foreignObject>
            </g>
        );
    }
);

export const CalendarButtonsContainer = styled(ButtonGroup)`
    &.MuiButtonGroup-root {
        background: white;
        border-radius: ${borderRadius.standard};
        margin: 8px;
        box-shadow: 0 0 10px -1px rgba(0, 0, 0, 0.5);
    }
`;

export const CalendarButton = styled(Button)`
    height: 40px;
    box-sizing: border-box;
    &.MuiIconButton-root {
        background: white;
        border-radius: ${borderRadius.standard};
    }
`;
